import React, { useEffect } from 'react';
import { CajaWrapper, StepLoader } from '@common/components/Caja/components';
import {
  useAdTypeState,
  useBrowserBackNavigationListener,
  useCajaVariant,
  useGetPaymentIntentParam,
  useJobAndBusinessLimits
} from '@common/components/Caja/hooks';
import { useTrackCajaEvent } from '@common/components/Caja/hooks/useTrackCajaEvent';
import { useGetCompletedUrl } from '@common/components/Caja/steps/PaymentJobStep/hooks/useGetCompletedUrl';
import { UpdateYourBusinessesModal } from '@common/components/UpdateYourBusinessesModal';
import {
  JobSummaryAndDesc,
  MultiStepJobEdit,
  MultiStepJobPost,
  PostJobSuccess,
  enhanceJobRouter,
  getImmediateStartPhrase,
  getRightToWorkDocument,
  isEditJobStep,
  isPostJobStep,
  paymentJobRouter
} from '@common/componentsV2/Caja/steps';
import { useBusinessState } from '@common/hooks/business';
import { useFetchJobData } from '@common/hooks/job';
import { useSteps } from '@common/hooks/multiStep';
import { useUpgradePermissions } from '@common/hooks/payment';
import { useUpdateSourceQuery } from '@common/hooks/useUpdateSourceQuery';
import { SupportedCountryCode as CountryCode } from '@seek/je-shared-data/lib/types/brand';
import { UpgradeableAdType, upgraded } from '@src/job';
import { JobLimitReached } from '@src/joraEmployer/pages/job/job-limit-reached';
import { toLowerCase } from '@src/lib/formatting';
import { useGetJobIdParam } from './hooks';
import {
  GetStepsParams,
  PostJobStepIds,
  getPageTitle,
  getSteps
} from './stepsInfo';

export const PostJob = () => {
  useUpdateSourceQuery();

  const urlJobId = useGetJobIdParam();
  const { hasPaymentIntent } = useGetPaymentIntentParam(); // for handling payment race condition; see https://jobseeker.atlassian.net/browse/AD-753?focusedCommentId=57731
  const { getOnCompletedUrl } = useGetCompletedUrl();
  const cajaVariant = useCajaVariant();
  const { isUpgradeable } = useUpgradePermissions();
  const [selectedAdType, setSelectedAdType] = useAdTypeState();
  const {
    showJobLimitReached,
    refetchJobLimit,
    showOverBusinessLimitBlockingModal
  } = useJobAndBusinessLimits();

  const {
    data: jobData,
    isLoading: fetchJobLoading,
    refetch
  } = useFetchJobData({
    jobId: urlJobId
  });

  const [createdJobCountryCode, setCountryCode] = useBusinessState(
    toLowerCase<CountryCode>(jobData?.countryCode)
  );
  const isJobUpgradable = isUpgradeable(createdJobCountryCode);

  const { currentStep, gotoNextStep, nextStep, gotoPreviousStep } = useSteps<
    GetStepsParams,
    PostJobStepIds
  >({
    data: {
      createdJobId: urlJobId,
      isUpgradeable: isJobUpgradable,
      hasJobUpgraded: upgraded(jobData?.adType) || hasPaymentIntent,
      selectedAdType
    },
    getSteps
  });

  useEffect(() => {
    if (urlJobId) refetch();
  }, [currentStep.id, urlJobId, refetch]);

  // Refetch the Job Limit if we're on a post job step to ensure we show the JobLimitReached component if the limit has been reached
  useEffect(() => {
    if (isPostJobStep(currentStep.id)) refetchJobLimit();
  }, [refetchJobLimit, currentStep.id]);

  useBrowserBackNavigationListener(gotoPreviousStep);

  useTrackCajaEvent({
    step: currentStep.id,
    variant: `post-job-${cajaVariant}`,
    jobId: urlJobId
  });

  if (fetchJobLoading) return <StepLoader />;

  if (showJobLimitReached && isPostJobStep(currentStep.id)) {
    return <JobLimitReached />;
  }

  const EnhanceJob = enhanceJobRouter({ cajaVariant });
  const PaymentJob = paymentJobRouter({ cajaVariant });

  return (
    <CajaWrapper title={getPageTitle(currentStep.id)}>
      {showOverBusinessLimitBlockingModal && <UpdateYourBusinessesModal />}

      {isPostJobStep(currentStep.id) && (
        <MultiStepJobPost
          stepInfo={{
            currentStepId: currentStep.id
          }}
          onCompleted={({ jobId: id, jobCountryCode }) => {
            setCountryCode(jobCountryCode);
            gotoNextStep({
              createdJobId: id,
              isUpgradeable: isUpgradeable(
                toLowerCase<CountryCode>(jobCountryCode)
              )
            });
          }}
          onNext={(data) => {
            if (data.jobCountryCode) setCountryCode(data.jobCountryCode);
            gotoNextStep(data);
          }}
          onBack={gotoPreviousStep}
        />
      )}
      {isEditJobStep(currentStep.id) && (
        <MultiStepJobEdit
          onCompleted={gotoNextStep}
          onNext={gotoNextStep}
          onBack={gotoPreviousStep}
          stepInfo={{
            jobId: urlJobId!,
            job: jobData!!,
            currentStepId: currentStep.id
          }}
        />
      )}
      {currentStep.id === 'enhanceJob' && (
        <EnhanceJob
          stepInfo={{
            jobId: urlJobId!,
            jobCountryCode: createdJobCountryCode!,
            disableAdTypeSelector: upgraded(jobData?.adType),
            initialAdType: upgraded(jobData?.adType)
              ? jobData?.adType
              : selectedAdType,
            businessId: jobData?.businessId!
          }}
          onCompleted={({ jobAdType, hasJobUpgraded }) => {
            setSelectedAdType(jobAdType);
            gotoNextStep({
              createdJobId: urlJobId!,
              selectedAdType: jobAdType,
              hasJobUpgraded: hasJobUpgraded || upgraded(jobData?.adType)
            });
          }}
          onBack={gotoPreviousStep}
        />
      )}
      {currentStep.id === 'payment' && (
        <PaymentJob
          stepInfo={{
            jobId: urlJobId!,
            jobCountryCode: createdJobCountryCode!,
            adType: selectedAdType as UpgradeableAdType,
            onCompletedUrl: getOnCompletedUrl(nextStep)
          }}
          onBack={gotoPreviousStep}
        />
      )}
      {currentStep.id === 'jobSummaryAndDesc' && (
        <JobSummaryAndDesc
          stepInfo={{
            jobId: urlJobId!,
            showAdTypeBadge: isJobUpgradable,
            adType: selectedAdType,
            rightToWorkDocument: getRightToWorkDocument(cajaVariant),
            immediateStartPhrase: getImmediateStartPhrase(createdJobCountryCode)
          }}
          onCompleted={gotoNextStep}
          onBack={gotoPreviousStep}
        />
      )}
      {currentStep.id === 'postJobSuccess' && (
        <PostJobSuccess
          stepInfo={{
            cajaVariant,
            jobUpgradeInfo: {
              isJobUpgradable: isJobUpgradable && !upgraded(selectedAdType),
              jobCountryCode: createdJobCountryCode!,
              jobId: urlJobId!
            }
          }}
        />
      )}
    </CajaWrapper>
  );
};
